import React, { useState } from 'react';
import { Dropdown, Modal } from 'react-bootstrap';
import { useBills } from '../contexts/BillsContext';
import { Bill, BILL_STATUS_CONFIG } from '../../../types/accounting';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { BillViewModal } from './BillViewModal';
import { formatDateAU, isDateOverdue } from '../../../utils/dateUtils';

const formatDateStr = formatDateAU;

interface BillListProps {
  onEditBill?: (bill: Bill) => void;
  onNewBill?: () => void;
  filterContent?: React.ReactNode;
}

export function BillList({ onEditBill, onNewBill, filterContent }: BillListProps) {
  const {
    bills,
    billsLoading,
    billsError,
    billFilters,
    voidBill,
    deleteDraftBill,
    billActionLoading,
  } = useBills();

  const [sortField, setSortField] = useState<keyof Bill | 'contactName'>('dueDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'void' | 'delete'; bill: Bill } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const isMobile = useIsMobile(768);

  const formatCurrency = (amount: number, currency = 'AUD') =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount);

  const isOverdue = (bill: Bill) => {
    if (bill.status !== 'APPROVED' && bill.status !== 'SENT') return false;
    if (!(bill.amountDue && bill.amountDue > 0)) return false;
    return isDateOverdue(bill.dueDate);
  };

  const handleVoid = async (bill: Bill) => {
    setConfirmAction(null);
    setActionError(null);
    try {
      await voidBill(bill.id);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to void bill');
      setTimeout(() => setActionError(null), 5000);
    }
  };

  const handleDeleteDraft = async (bill: Bill) => {
    setConfirmAction(null);
    setActionError(null);
    try {
      await deleteDraftBill(bill.id);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to delete bill');
      setTimeout(() => setActionError(null), 5000);
    }
  };

  const handleSort = (field: keyof Bill | 'contactName') => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedBills = [...bills].sort((a, b) => {
    let aVal: any = a[sortField as keyof Bill];
    let bVal: any = b[sortField as keyof Bill];
    if (sortField === 'contactName') {
      aVal = a.contactName || '';
      bVal = b.contactName || '';
    }
    if (sortField === 'dueDate' || sortField === 'date') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const strA = String(aVal || '').toLowerCase();
    const strB = String(bVal || '').toLowerCase();
    return sortDir === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
  });

  const totalCount = sortedBills.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedBills = sortedBills.slice(startIdx, startIdx + pageSize);

  const statusArray = Array.isArray(billFilters.status) ? billFilters.status : (billFilters.status ? [billFilters.status] : []);
  const emptyMessage = statusArray.length > 0 ? 'No bills match the selected filters.' : 'No bills found.';

  const SortHeader = ({ field, label }: { field: keyof Bill | 'contactName'; label: string }) => (
    <th className="min-w-100px cursor-pointer" onClick={() => handleSort(field)}>
      <div className="d-flex align-items-center">
        {label}
        {sortField === field && <i className={`bi bi-chevron-${sortDir === 'asc' ? 'up' : 'down'} ms-1`} />}
      </div>
    </th>
  );

  if (billsLoading) {
    return (
      <div className="card">
        <div className="card-header border-0 pt-5 px-4 px-md-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Bills</span>
            <span className="text-muted mt-1 fw-semibold fs-7">Loading…</span>
          </h3>
        </div>
        {filterContent}
        <div className="card-body py-3">
          <div className="table-responsive">
            <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr className="fw-bold text-muted">
                  <th>Bill #</th><th>Supplier</th><th>Total</th><th>Amount Due</th><th>Date Generated</th><th>Due Date</th><th>Status</th><th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                  <tr key={i}>
                    <td colSpan={8}><span className="placeholder col-12" style={{ height: 20 }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (billsError) {
    return (
      <div className="card">
        <div className="card-header border-0 pt-5 px-4 px-md-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Bills</span>
          </h3>
        </div>
        {filterContent}
        <div className="card-body py-10 text-center">
          <i className="bi bi-exclamation-triangle text-danger fs-1" />
          <p className="text-danger mt-3">{billsError}</p>
        </div>
      </div>
    );
  }

  if (bills.length === 0) {
    return (
      <div className="card">
        <div className="card-header border-0 pt-5 px-4 px-md-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Bills</span>
            <span className="text-muted mt-1 fw-semibold fs-7">0 bills</span>
          </h3>
        </div>
        {filterContent}
        <div className="card-body py-10 text-center">
          <i className="bi bi-receipt text-muted fs-1" />
          <p className="text-muted mt-3">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header border-0 pt-4 pt-md-5 px-4 px-md-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">Bills</span>
          <span className="text-muted mt-1 fw-semibold fs-7">{totalCount} bill{totalCount !== 1 ? 's' : ''}</span>
        </h3>
        {onNewBill && (
          <div className="card-toolbar accounting-card-toolbar">
            <button className="btn btn-sm btn-success" onClick={onNewBill}>
              <i className="bi bi-plus-lg me-2" /> New Bill
            </button>
          </div>
        )}
      </div>
      {filterContent}
      <div className="card-body py-3">
        {actionError && (
          <div className="alert alert-danger mb-3 py-2">
            <i className="bi bi-exclamation-triangle me-2" /> {actionError}
          </div>
        )}

        {isMobile && (
          <div className="d-md-none d-flex flex-column gap-2">
            {paginatedBills.map(bill => {
              const config = BILL_STATUS_CONFIG[bill.status] || { label: bill.status, badgeClass: 'badge-light-secondary' };
              const overdue = isOverdue(bill);
              return (
                <div
                  key={bill.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedBill(bill)}
                  onKeyDown={e => e.key === 'Enter' && setSelectedBill(bill)}
                  className="border rounded-3 p-3 bg-body bill-mobile-card"
                  style={{ transition: 'background-color 0.15s' }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className="fw-bold fs-6 text-gray-800">{bill.billNumber}</span>
                    <span className={`badge ${config.badgeClass}`}>{config.label}</span>
                  </div>
                  <div className="text-muted fs-8 mb-1">{bill.contactName || '—'}</div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className={`fw-bold ${overdue ? 'text-danger' : 'text-gray-800'}`}>
                      {formatCurrency(bill.amountDue ?? 0, bill.currencyCode)}
                    </span>
                    <span className={`fs-8 ${overdue ? 'text-danger' : 'text-muted'}`}>
                      Due {formatDateStr(bill.dueDate)}{overdue && <i className="bi bi-exclamation-circle-fill ms-1" />}
                    </span>
                  </div>
                  <div className="text-muted fs-8 mt-1">
                    Generated {formatDateStr(bill.date)}
                  </div>
                  <div className="mt-2 pt-2 border-top d-flex align-items-center justify-content-end">
                    <i className="bi bi-chevron-right text-muted" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={`table-responsive ${isMobile ? 'd-none' : ''}`}>
          <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
            <thead>
              <tr className="fw-bold text-muted">
                <SortHeader field="billNumber" label="Bill #" />
                <th className="min-w-150px cursor-pointer" onClick={() => handleSort('contactName')}>
                  <div className="d-flex align-items-center">
                    Supplier
                    {sortField === 'contactName' && <i className={`bi bi-chevron-${sortDir === 'asc' ? 'up' : 'down'} ms-1`} />}
                  </div>
                </th>
                <SortHeader field="total" label="Total" />
                <SortHeader field="amountDue" label="Amount Due" />
                <SortHeader field="date" label="Date Generated" />
                <SortHeader field="dueDate" label="Due Date" />
                <th className="min-w-100px">Status</th>
                <th className="min-w-80px text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBills.map(bill => {
                const config = BILL_STATUS_CONFIG[bill.status] || { label: bill.status, badgeClass: 'badge-light-secondary' };
                const overdue = isOverdue(bill);
                return (
                  <tr key={bill.id}>
                    <td>
                      <button
                        className="btn btn-link p-0 text-start text-hover-primary fw-bold fs-6"
                        onClick={() => setSelectedBill(bill)}
                        title="View bill details"
                      >
                        {bill.billNumber}
                      </button>
                      {bill.reference && <span className="text-muted d-block fs-7">{bill.reference}</span>}
                    </td>
                    <td>{bill.contactName || '—'}</td>
                    <td>
                      <span className="text-dark fw-semibold">{formatCurrency(bill.total ?? 0, bill.currencyCode)}</span>
                    </td>
                    <td>
                      <span className={`text-dark fw-bold ${overdue ? 'text-danger' : ''}`}>
                        {formatCurrency(bill.amountDue ?? 0, bill.currencyCode)}
                      </span>
                    </td>
                    <td>{formatDateStr(bill.date)}</td>
                    <td>
                      <span className={overdue ? 'text-danger fw-semibold' : ''}>
                        {formatDateStr(bill.dueDate)}
                        {overdue && <i className="bi bi-exclamation-circle-fill text-danger ms-2" title="Overdue" />}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${config.badgeClass}`}>{config.label}</span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <button
                          className="btn btn-sm btn-icon btn-light-primary"
                          onClick={() => onEditBill?.(bill)}
                          title="Edit bill"
                          disabled={bill.status === 'PAID' || bill.status === 'VOIDED'}
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <Dropdown align="end">
                          <Dropdown.Toggle variant="light" size="sm" className="btn-icon" id={`bill-actions-${bill.id}`} disabled={!!billActionLoading}>
                            <i className="bi bi-three-dots-vertical" />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {(bill.status === 'APPROVED' || bill.status === 'SENT') && (
                              <Dropdown.Item onClick={() => setConfirmAction({ type: 'void', bill })} disabled={billActionLoading === bill.id}>
                                <i className="bi bi-x-circle me-2" /> Void bill
                              </Dropdown.Item>
                            )}
                            {bill.status === 'DRAFT' && (
                              <Dropdown.Item onClick={() => setConfirmAction({ type: 'delete', bill })} disabled={billActionLoading === bill.id} className="text-danger">
                                <i className="bi bi-trash me-2" /> Delete draft
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 pt-3 border-top">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted fs-8">Showing {startIdx + 1}–{Math.min(startIdx + pageSize, totalCount)} of {totalCount}</span>
              <select className="form-select form-select-sm" style={{ width: 'auto' }} value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
              </select>
              <span className="text-muted fs-8">per page</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <button type="button" className="btn btn-sm btn-light" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>
                <i className="bi bi-chevron-left" />
              </button>
              <span className="px-2 fs-8 text-muted">Page {currentPage} of {totalPages}</span>
              <button type="button" className="btn btn-sm btn-light" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>

      <BillViewModal
        show={!!selectedBill}
        bill={selectedBill}
        onHide={() => setSelectedBill(null)}
        onEdit={onEditBill}
      />

      <Modal show={!!confirmAction} onHide={() => setConfirmAction(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmAction?.type === 'void' ? 'Void bill' : 'Delete draft'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction && (
            <p className="mb-0">
              {confirmAction.type === 'void'
                ? 'Are you sure you want to void this bill? This cannot be undone.'
                : 'Are you sure you want to delete this draft bill? This cannot be undone.'}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-light" onClick={() => setConfirmAction(null)}>Cancel</button>
          <button
            className={confirmAction?.type === 'delete' ? 'btn btn-danger' : 'btn btn-warning'}
            onClick={() => confirmAction && (confirmAction.type === 'void' ? handleVoid(confirmAction.bill) : handleDeleteDraft(confirmAction.bill))}
          >
            {confirmAction?.type === 'void' ? 'Void' : 'Delete'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default BillList;
